// Slug utilities for product deep links (Meta Ads, WhatsApp, QR Code, Google Ads).
// Pure functions — no side effects, no dependencies.

// Unicode combining diacritical marks U+0300–U+036F, stored as escape sequences.
const DIACRITICS_RE = new RegExp("[̀-ͯ]", "g");

export function slugify(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(DIACRITICS_RE, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

// Build a guaranteed-unique product URL slug.
// Format: "menu-50-piezas--64fabc123"
// The "--" separator is never produced by slugify() so parsing is unambiguous.
export function buildProductSlug(product) {
  return `${slugify(product.name)}--${product.id}`;
}

// Find product by URL slug.
// Priority:
//  1) ID-anchored "slug--productId" — guaranteed match via product.id
//  2) Exact name slug — backward compat with old links
//  3) Token-based fuzzy — all slug tokens (≥2 chars) appear in product slug
export function findProductBySlug(products, slug) {
  if (!slug || !products?.length) return null;

  // 1. ID-anchored: split on "--" and look up by product ID
  const sepIdx = slug.indexOf("--");
  if (sepIdx > 0) {
    const productId = slug.slice(sepIdx + 2);
    const byId = products.find(
      (p) => String(p.id) === productId || String(p._id) === productId
    );
    if (byId) return byId;
  }

  // 2. Exact name-slug match (backward compat)
  const exact = products.find((p) => slugify(p.name) === slug);
  if (exact) return exact;

  // 3. Token-based fuzzy: all tokens appear in product slug
  const tokens = slug.split("-").filter((t) => t.length >= 2);
  if (tokens.length >= 2) {
    const fuzzy = products.find((p) => {
      const ps = slugify(p.name);
      return tokens.every((token) => ps.includes(token));
    });
    if (fuzzy) return fuzzy;
  }

  return null;
}

// Find combo whose slugified name matches slug.
export function findComboBySlug(combos, slug) {
  if (!slug || !combos?.length) return null;
  return combos.find((c) => slugify(c.name) === slug) ?? null;
}

// Canonical product link — ID-anchored, uses /product/ route.
export function buildProductLink(origin, restaurantSlug, product) {
  return `${origin}/m/${restaurantSlug}/product/${buildProductSlug(product)}`;
}

// Legacy: kept for backward compat, no longer used for new links.
export function buildOfferLink(origin, restaurantId, productName) {
  return `${origin}/m/${restaurantId}/oferta/${slugify(productName)}`;
}
